import { Component, DestroyRef, inject, OnInit, Signal, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatCheckboxChange, MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { UiButtonComponent } from '@app/shared/ui/button/ui-button.component';

import { SelectLocationListItemComponent } from '@app/shared/components/select-location-list-item/select-location-list-item.component';
import { ActiveProjectService } from '@app/shared/services/active-project.service';
import { LocationsDataService } from '@app/shared/services/locations-data.service';
import { ProjectsDataService } from '@app/shared/services/projects-data.service';
import { DuplicateLocationsDialogData, Location } from '@app/shared/types/models/location/location.model';
import { Project } from '@app/shared/types/models/project/project.model';
import { UiDialogComponent } from '@app/shared/ui/dialog/ui-dialog.component';
import { TranslatePipe } from '@ngx-translate/core';
import { finalize } from 'rxjs';

@Component({
  selector: 'proffeo-duplicate-location-dialog',
  imports: [
    SelectLocationListItemComponent,
    MatDialogModule,
    TranslatePipe,
    UiDialogComponent,
    MatCheckboxModule,
    UiButtonComponent
  ],
  templateUrl: './duplicate-location-dialog.component.html'
})
export class DuplicateLocationDialogComponent implements OnInit {
  private readonly projectService: ProjectsDataService = inject(ProjectsDataService);
  private readonly locationsDataService: LocationsDataService = inject(LocationsDataService);
  private readonly dialogRef: MatDialogRef<DuplicateLocationDialogComponent> = inject(
    MatDialogRef<DuplicateLocationDialogComponent>
  );
  private readonly dialogData: DuplicateLocationsDialogData = inject(MAT_DIALOG_DATA);
  private readonly activeProjectService: ActiveProjectService = inject(ActiveProjectService);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);

  protected readonly isSaving = signal(false);
  protected readonly duplicateChildren = signal(false);
  protected readonly duplicateAttachments = signal(true);
  protected location: Location;
  protected selectedLocation: Location;
  protected availableLocations: Signal<Location[]>;
  protected selectedProject: Signal<Project> = this.activeProjectService.activeProject;

  public ngOnInit(): void {
    this.availableLocations = this.dialogData.locations;
    this.location = this.dialogData.locationsToDuplicate[0];
  }

  public save(): void {
    if (this.isSaving()) {
      return;
    }
    this.isSaving.set(true);
    this.locationsDataService
      .duplicateLocations(
        this.selectedProject()?.id,
        this.dialogData.locationsToDuplicate.map(location => location.id),
        this.selectedLocation?.id,
        this.duplicateAttachments(),
        this.duplicateChildren()
      )
      .pipe(
        finalize(() => this.isSaving.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.dialogRef.close());
  }

  public onClose(): void {
    this.dialogRef.close();
  }

  protected onDuplicateChildrenChange(event: MatCheckboxChange): void {
    this.duplicateChildren.set(event.checked);
  }

  protected onDuplicateAttachmentsChange(event: MatCheckboxChange): void {
    this.duplicateAttachments.set(event.checked);
  }
}
